import { useGetTheme } from '@pba/react-theme';
import { type ThemeConfig, message } from 'antd';
import { useEffect, useMemo } from 'react';

export const useGenerateTheme = (): ThemeConfig => {
  const theme = useGetTheme();

  useEffect(() => {
    message.config({ top: theme.reactComponents.message.top });
  }, [theme]);

  return useMemo(() => {
    const { border, color, fontFamily, fontSize, fontWeight, reactComponents, shadow, spacing } = theme;

    return {
      components: {
        Alert: {
          colorErrorBg: color.error.background,
          colorErrorBorder: color.error.default,
          colorInfoBg: color.info.background,
          colorInfoBorder: color.info.default,
          colorSuccessBg: color.success.background,
          colorSuccessBorder: color.success.default,
          colorWarningBg: color.warning.background,
          colorWarningBorder: color.warning.default,
        },
        Badge: {
          fontSize: reactComponents.badge.statusTextFontSize.value,
          indicatorHeight: reactComponents.badge.indicatorHeight.value,
          statusSize: reactComponents.badge.statusSize.value,
          textFontSize: reactComponents.badge.countTextFontSize.value,
        },
        Button: {
          borderRadius: border.radius.base.value,
          borderRadiusLG: border.radius.base.value,
          colorPrimaryBg: color.primary.default6,
          colorPrimaryBgHover: color.primary.default10,
          contentFontSizeLG: fontSize.content.base.value,
          controlHeight: reactComponents.button.size.middle.value,
          controlHeightLG: reactComponents.button.size.large.value,
          controlHeightSM: reactComponents.button.size.small.value,
          dangerShadow: shadow.danger,
          defaultHoverBorderColor: color.text.default,
          defaultHoverColor: color.text.default,
          defaultShadow: shadow.default,
          primaryShadow: shadow.primary,
          textHoverBg: color.surface.subtle,
        },
        Card: {
          borderRadiusLG: border.radius.lg.value,
          marginLG: spacing.ms.value,
          paddingLG: spacing.ms.value,
        },
        Collapse: {
          headerPadding: `${spacing.xxs.value}${spacing.xxs.unit} 0`,
        },
        Dropdown: {
          paddingBlock: spacing.xs.value,
        },
        Menu: {
          darkItemBg: color.surface.dark,
          darkItemSelectedBg: color.surface.dark,
          darkPopupBg: color.surface.dark,
          darkSubMenuItemBg: color.surface.dark,
          iconMarginInlineEnd: spacing.lg.value,
          iconSize: spacing.ms.value,
          itemBorderRadius: 0,
          itemHoverBg: 'transparent',
          itemHoverColor: color.primary.default,
          itemMarginBlock: reactComponents.menu.itemMarginBlock,
          itemMarginInline: 0,
          itemPaddingInline: spacing.xs.value,
          itemSelectedBg: 'transparent',
        },
        Modal: {
          borderRadiusLG: border.radius.lg.value,
        },
        Radio: {
          buttonBg: 'transparent',
          buttonSolidCheckedActiveBg: color.primary.default6,
          buttonSolidCheckedBg: color.primary.default6,
          buttonSolidCheckedColor: color.primary.default,
          buttonSolidCheckedHoverBg: color.primary.default6,
          dotSize: 8,
        },
        Rate: {
          starBg: color.surface.default,
          starColor: color.accent.default,
          starSize: fontSize.content.lg.value,
          starSizeSM: fontSize.content.sm.value,
        },
        Tag: {
          fontSize: fontSize.text.sm.value,
          fontSizeIcon: fontSize.text.sm.value,
        },
        Typography: {
          fontSizeHeading1: fontSize.heading.h1.value,
          fontSizeHeading2: fontSize.heading.h2.value,
          fontSizeHeading3: fontSize.heading.h3.value,
          fontSizeHeading4: fontSize.heading.h4.value,
          fontSizeHeading5: fontSize.heading.h5.value,
          fontWeightStrong: fontWeight.bold,
        },
      },
      token: {
        colorBgContainerDisabled: color.surface.disabled,
        colorError: color.error.default,
        colorInfo: color.info.default,
        colorPrimary: color.primary.default,
        colorSuccess: color.success.default,
        colorText: color.text.default,
        colorTextDisabled: color.text.default,
        colorTextHeading: color.text.heading,
        colorWarning: color.warning.default,
        fontFamily,
      },
    };
  }, [theme]);
};
